// src/components/MallTablesTab.js
// Mall admin table management — DB-backed tables with per-table enable/disable and inline QR
import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { QRImage } from './QRCodeGenerator';

const BASE_URL = process.env.REACT_APP_BASE_URL || window.location.origin;

function timeAgo(iso) {
  if (!iso) return 'Never';
  const secs = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (secs < 60)    return 'Just now';
  if (secs < 3600)  return `${Math.floor(secs / 60)}m ago`;
  if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
  return `${Math.floor(secs / 86400)}d ago`;
}

export default function MallTablesTab({ mall }) {
  const [tables,   setTables]   = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [msg,      setMsg]      = useState('');
  const [busy,     setBusy]     = useState(false);
  const [single,   setSingle]   = useState({ tableNo: '', label: '' });
  const [bulk,     setBulk]     = useState({ prefix: 'T', from: 1, to: 20 });
  const [selected, setSelected] = useState(new Set());
  const [qrOpen,   setQrOpen]   = useState(null); // tableId whose QR is expanded

  const flash = (t) => { setMsg(t); setTimeout(() => setMsg(''), 4000); };
  const errOf = (e) => '❌ ' + (e.response?.data?.error || e.message || 'Request failed');

  const load = useCallback(async () => {
    try {
      const r = await api.get('/mall/tables');
      setTables(r.data || []);
    } catch (e) { flash(errOf(e)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const run = async (fn) => {
    setBusy(true);
    try { await fn(); } catch (e) { flash(errOf(e)); } finally { setBusy(false); }
  };

  const addSingle = () => run(async () => {
    if (!single.tableNo.trim()) return flash('❌ Table number is required');
    await api.post('/mall/tables', single);
    setSingle({ tableNo: '', label: '' });
    flash('✅ Table added');
    await load();
  });

  const addBulk = () => run(async () => {
    const r = await api.post('/mall/tables/bulk', bulk);
    flash(`✅ ${r.data.created} added${r.data.skipped ? `, ${r.data.skipped} already existed` : ''}`);
    await load();
  });

  const toggleTable = (t) => run(async () => {
    await api.patch(`/mall/tables/${t.id}`, { isActive: !t.isActive });
    setTables(ts => ts.map(x => x.id === t.id ? { ...x, isActive: !t.isActive } : x));
  });

  const bulkStatus = (isActive) => run(async () => {
    await api.patch('/mall/tables/bulk-status', { tableIds: [...selected], isActive });
    setSelected(new Set());
    flash(`✅ ${isActive ? 'Enabled' : 'Disabled'} selected tables`);
    await load();
  });

  const remove = (t) => run(async () => {
    if (!window.confirm(`Delete table ${t.tableNo}? Its QR code will stop working.`)) return;
    await api.delete(`/mall/tables/${t.id}`);
    setSelected(s => { const n = new Set(s); n.delete(t.id); return n; });
    await load();
  });

  const toggleSel = (id) => setSelected(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const allSelected = tables.length > 0 && selected.size === tables.length;

  if (!mall?.slug) return (
    <div className="page">
      <div style={{ padding:'10px 14px', background:'#3d1a1a', borderRadius:8, fontSize:13, color:'var(--red)' }}>
        ⚠️ Mall needs a URL slug. Contact super admin.
      </div>
    </div>
  );

  const th = { textAlign:'left', padding:'8px 10px', borderBottom:'1px solid var(--border)', fontSize:11, color:'var(--text3)', textTransform:'uppercase' };
  const td = { padding:'10px 10px', verticalAlign:'middle' };

  return (
    <div className="page">
      <div className="page-title" style={{ marginBottom:4 }}>📱 Table QR Codes</div>
      <div className="page-sub" style={{ marginBottom:24 }}>
        Register your food court tables. Once any table is registered, only registered, enabled tables can order via QR.
      </div>

      {msg && (
        <div style={{ padding:'10px 16px', borderRadius:8, marginBottom:16, fontSize:13,
          background: msg.startsWith('✅') ? '#1a3d2a' : '#3d1a1a',
          color:      msg.startsWith('✅') ? 'var(--green)' : 'var(--red)',
          border:     `1px solid ${msg.startsWith('✅') ? 'var(--green)' : 'var(--red)'}` }}>{msg}</div>
      )}

      <div className="card" style={{ marginBottom:16 }}>
        <div style={{ fontWeight:700, fontSize:15, marginBottom:16 }}>➕ Add Tables</div>
        <div className="grid2" style={{ gap:24 }}>
          <div>
            <div className="section-label" style={{ marginBottom:6 }}>Single table</div>
            <div style={{ display:'flex', gap:8 }}>
              <input placeholder="Table no. e.g. T1, VIP-2" value={single.tableNo} style={{ flex:1 }}
                onChange={e => setSingle(s => ({ ...s, tableNo: e.target.value }))} />
              <input placeholder="Label (optional)" value={single.label} style={{ flex:1 }}
                onChange={e => setSingle(s => ({ ...s, label: e.target.value }))} />
              <button className="btn btn-primary btn-sm" disabled={busy} onClick={addSingle}>Add</button>
            </div>
          </div>
          <div>
            <div className="section-label" style={{ marginBottom:6 }}>Bulk range</div>
            <div style={{ display:'flex', gap:8, alignItems:'center' }}>
              <input placeholder="Prefix" value={bulk.prefix} style={{ width:80 }}
                onChange={e => setBulk(b => ({ ...b, prefix: e.target.value }))} />
              <input type="number" min="0" value={bulk.from} style={{ width:80 }}
                onChange={e => setBulk(b => ({ ...b, from: e.target.value }))} />
              <span style={{ color:'var(--text3)' }}>to</span>
              <input type="number" min="0" value={bulk.to} style={{ width:80 }}
                onChange={e => setBulk(b => ({ ...b, to: e.target.value }))} />
              <button className="btn btn-primary btn-sm" disabled={busy} onClick={addBulk}>
                Add {bulk.prefix}{bulk.from}–{bulk.prefix}{bulk.to}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="card">
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:14 }}>
          <div style={{ fontWeight:700, fontSize:15 }}>🪑 Tables ({tables.length})</div>
          {selected.size > 0 && (
            <div style={{ display:'flex', gap:8, alignItems:'center' }}>
              <span style={{ fontSize:12, color:'var(--text2)' }}>{selected.size} selected</span>
              <button className="btn btn-ghost btn-sm" disabled={busy} onClick={() => bulkStatus(true)}>Enable selected</button>
              <button className="btn btn-ghost btn-sm" disabled={busy} onClick={() => bulkStatus(false)}>Disable selected</button>
            </div>
          )}
        </div>

        {loading ? (
          <div style={{ padding:24, textAlign:'center', color:'var(--text3)' }}>Loading…</div>
        ) : tables.length === 0 ? (
          <div style={{ padding:32, textAlign:'center', color:'var(--text3)', fontSize:13 }}>
            No tables configured yet. Add tables above to enable table-specific QR ordering.
          </div>
        ) : (
          <div style={{ overflowX:'auto' }}>
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
              <thead>
                <tr>
                  <th style={{ ...th, width:30 }}>
                    <input type="checkbox" checked={allSelected}
                      onChange={() => setSelected(allSelected ? new Set() : new Set(tables.map(t => t.id)))} />
                  </th>
                  {['Table No', 'Label', 'Status', 'Last Order', 'QR', ''].map((h, i) => <th key={i} style={th}>{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {tables.map(t => {
                  const url = `${BASE_URL}/order/mall/${mall.slug}?table=${encodeURIComponent(t.tableNo)}`;
                  return (
                    <React.Fragment key={t.id}>
                      <tr style={{ borderBottom:'1px solid var(--border)', opacity: t.isActive ? 1 : 0.6 }}>
                        <td style={td}><input type="checkbox" checked={selected.has(t.id)} onChange={() => toggleSel(t.id)} /></td>
                        <td style={{ ...td, fontWeight:600 }}>{t.tableNo}</td>
                        <td style={{ ...td, color:'var(--text2)' }}>{t.label || '—'}</td>
                        <td style={td}>
                          <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                            <div className={`toggle${t.isActive ? ' on' : ''}`} onClick={() => !busy && toggleTable(t)}
                              style={{ cursor:'pointer', background: t.isActive ? 'var(--green)' : 'var(--red)' }} />
                            <span style={{ fontSize:12, color: t.isActive ? 'var(--green)' : 'var(--red)' }}>
                              {t.isActive ? 'Enabled' : 'Disabled'}
                            </span>
                          </div>
                        </td>
                        <td style={{ ...td, color:'var(--text2)' }}>{timeAgo(t.lastOrderAt)}</td>
                        <td style={td}>
                          <button className="btn btn-ghost btn-sm" onClick={() => setQrOpen(qrOpen === t.id ? null : t.id)}>
                            {qrOpen === t.id ? '▲ Hide' : '▼ QR'}
                          </button>
                        </td>
                        <td style={td}>
                          <button className="btn btn-ghost btn-sm" style={{ color:'var(--red)' }} disabled={busy} onClick={() => remove(t)}>🗑</button>
                        </td>
                      </tr>
                      {qrOpen === t.id && (
                        <tr>
                          <td colSpan={7} style={{ padding:'16px 10px', background:'var(--bg3)' }}>
                            <QRImage url={url} label={`Table ${t.tableNo}`} size={180} />
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
